import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Row, Col, Table, Button, message } from 'antd';
import PageLoader from '../../../../../../components/PageLoader/PageLoader';
import * as HttpStatus from 'http-status-codes';
import PlanProcessBar from '../../../../../../components/ProcessBar/PlanProcessBar';
import Card from '../../../../../../components/Card/Card';
import InfoBox from '../../../../../../components/InfoBox/InfoBox';
import InformationPanel from '../../../../../../components/InformationPanel/InformationPanel';
import Slider from '../../../../../../components/Slider/Slider';
import { fetchTacticalBia, createTacticalBia, updateTacticalBia } from '../../BiaAction';
import { fetchBranch } from '../../../../../Organisation/scenes/Branch/BranchAction';
import { orgIdSelector } from '../../../../../Organisation/OrganisationSelectors';
import { planIdSelector, mtpdDaysSelector } from '../../../Plan/PlanSelectors';
import { departmentListSelector } from '../../../Department/DepartmentSelectors';
import { tacticalBiasSelector, loadingTacticalBiasSelector } from '../../BiaSelectors';
import { MSG_SAVE_SUCCESS, MSG_ERROR, MSG_PAYMENT_REQUIRED } from '../../../../../../constants';
import { getApiErrorMessage } from '../../../../../../utils/httpError';

const BIA_STEP_EXPLANATION = {
  title: 'Recovery Time Objective and Recovery Point Objective (RTO-RPO)',
  description:
    'For each Business Unit you need to identify what the Recovery Time Objective and Recovery Point Objective (RTO-RPO) will be.',
};

class TacticalBia extends Component {
  state = { data: null };

  componentDidMount() {
    this.loadData();
  }

  toSafeNumber = (value, fallback = 0) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : fallback;
  };

  toBoundedDayValue = (value, max) => {
    const safeMax = Math.max(this.toSafeNumber(max, 0), 0);
    const safeValue = Math.max(this.toSafeNumber(value, 0), 0);
    return Math.min(safeValue, safeMax);
  };

  formatDayLabel = (value) => {
    const safeValue = this.toSafeNumber(value, 0);
    return safeValue > 1 ? `${safeValue} days` : `${safeValue} day`;
  };

  loadData = async () => {
    const { orgId, planId, fetchBranch, fetchTacticalBia } = this.props;
    await fetchBranch(orgId);
    await fetchTacticalBia(planId);
    const { departmentList, tacticalBias, mtpdDays = 0 } = this.props;
    const safeMtpdDays = Math.max(this.toSafeNumber(mtpdDays, 0), 0);
    const safeDepartmentList = Array.isArray(departmentList) ? departmentList : [];
    const safeTacticalBias = Array.isArray(tacticalBias) ? tacticalBias : [];
    const data = safeDepartmentList.map((departmentObj) => {
      const { departmentId, name } = departmentObj;
      const tacticalBia = safeTacticalBias.find((tacticalBiaObj) => tacticalBiaObj.departmentId === departmentId);
      const tacticalBiaId = tacticalBia ? tacticalBia.tacticalBiaId : null;
      const rto = tacticalBia ? this.toBoundedDayValue(tacticalBia.rto, safeMtpdDays) : 0;
      const rpo = tacticalBia ? this.toBoundedDayValue(tacticalBia.rpo, safeMtpdDays) : 0;
      return { departmentId, name, tacticalBiaId, rto, rpo };
    });
    this.setState({ data });
  };

  onRtoChange = (value, departmentId) => {
    const { data = [] } = this.state;
    const index = data.findIndex((item) => item.departmentId === departmentId);
    if (index < 0) return;
    data[index].rto = value;
    this.setState({ data });
  };

  onRpoChange = (value, departmentId) => {
    const { data = [] } = this.state;
    const index = data.findIndex((item) => item.departmentId === departmentId);
    if (index < 0) return;
    data[index].rpo = value;
    this.setState({ data });
  };

  onSave = async () => {
    const { planId, fetchTacticalBia, createTacticalBia, updateTacticalBia } = this.props;
    const { data } = this.state;
    const tacticalBiasToCreate = [];
    const tacticalBiasToUpdate = [];
    data.forEach((obj) => {
      if (obj.tacticalBiaId) {
        tacticalBiasToUpdate.push(obj);
      } else {
        tacticalBiasToCreate.push(obj);
      }
    });
    const promises = [];
    if (tacticalBiasToCreate.length > 0) {
      promises.push(createTacticalBia(planId, tacticalBiasToCreate));
    }
    if (tacticalBiasToUpdate.length > 0) {
      promises.push(updateTacticalBia(planId, tacticalBiasToUpdate));
    }
    if (promises.length > 0) {
      try {
        await Promise.all(promises);
        await fetchTacticalBia(planId);
        message.success(MSG_SAVE_SUCCESS);
      } catch (err) {
        switch (err.status) {
          case HttpStatus.PAYMENT_REQUIRED:
            message.error(MSG_PAYMENT_REQUIRED);
            break;
          default:
            message.error(getApiErrorMessage(err, MSG_ERROR));
        }
      }
    }
  };

  render() {
    const { mtpdDays, loading } = this.props;
    const { data } = this.state;
    const safeMtpdDays = Math.max(this.toSafeNumber(mtpdDays, 0), 0);
    const columns = [
      {
        key: 'department',
        title: 'Business Units',
        width: '20%',
        render: (text, record) => <span>{record.name}</span>,
      },
      {
        key: 'rpo',
        title: <div className="table-header-cell-rpo">RPO</div>,
        width: '15%',
        render: (text, record) => <div>{this.formatDayLabel(record.rpo)}</div>,
      },
      {
        key: 'rto_slider',
        title: (
          <div>
            Range from: MIN (0 hours) - MAX
            <span className="table-header-cell-mtpd"> (MTPD: {safeMtpdDays} days)</span>
          </div>
        ),
        colSpan: 3,
        render: (text, record) => (
          <Slider
            min={0}
            max={safeMtpdDays}
            value={this.toBoundedDayValue(record.rpo, safeMtpdDays)}
            color="#aabd8c"
            reversed
            onChange={(value) => this.onRpoChange(value, record.departmentId)}
            style={{ marginRight: '-16px' }}
          />
        ),
      },
      {
        key: 'divider',
        colSpan: 0,
        width: 1,
        className: 'table-cell-divider',
      },
      {
        key: 'rpo_slider',
        colSpan: 0,
        render: (text, record) => (
          <Slider
            min={0}
            max={safeMtpdDays}
            value={this.toBoundedDayValue(record.rto, safeMtpdDays)}
            color="#dd6031"
            onChange={(value) => this.onRtoChange(value, record.departmentId)}
            style={{ marginLeft: '-16px' }}
          />
        ),
      },
      // {
      //   key: 'rto_slider',
      //   title: (
      //     <div>
      //       Range from: MIN (0 hours) - MAX<span className="table-header-cell-mtpd"> (MTPD: {mtpdDays} days)</span>
      //     </div>
      //   ),
      //   render: (text, record) => (
      //     <div style={{ display: 'flex' }}>
      //       <AntSlider
      //         min={0}
      //         max={mtpdDays}
      //         value={record.rto}
      //         disabled={loading}
      //         tipFormatter={value => `RTO: ${value} days`}
      //         onChange={value => this.onRtoChange(value, record.departmentId)}
      //         className="rto-slider"
      //       />
      //       <AntSlider
      //         min={0}
      //         max={mtpdDays}
      //         value={record.rpo}
      //         disabled={loading}
      //         tipFormatter={value => `RPO: ${value} days`}
      //         onChange={value => this.onRpoChange(value, record.departmentId)}
      //         className="rpo-slider"
      //       />
      //     </div>
      //   )
      // },
      {
        key: 'rto',
        title: <div className="table-header-cell-rto">RTO</div>,
        width: '15%',
        render: (text, record) => <div>{this.formatDayLabel(record.rto)}</div>,
      },
    ];

    return (
      <>
        <PlanProcessBar color="#343579" title="Tactical Business Impact Analysis" />
        <InfoBox
          color="#343579"
          guidance={
            <>
              <strong>What to do:</strong> for each Business Unit, set a <strong>Recovery Time Objective (RTO)</strong>{' '}
              and <strong>Recovery Point Objective (RPO)</strong>. Both must fall within your chosen Maximum Tolerable
              Period of Disruption (MTPD).
            </>
          }
          message="For each Business Unit you need to identify what the Recovery Time Objective and Recovery Point Objective (RTO-RPO) will be."
          more
          video={
            <iframe
              title="rto-rpo-info-video"
              src="https://www.youtube.com/embed/guYNMFGYqbk?rel=0&amp;showinfo=0"
              frameBorder="0"
              allow="autoplay; encrypted-media"
              allowFullScreen
              className="responsive-video"
            />
          }
        />
        {data ? (
          <div className="bia-container">
            <Row gutter={20}>
              <Col span={20}>
                <Card color="#343579" title="Recovery Time Objective and Recovery Point Objective (RTO-RPO)">
                  <div className="tactical-bia-inner">
                    <Table
                      columns={columns}
                      dataSource={data}
                      rowKey={(record) => record.departmentId}
                      pagination={false}
                      locale={{ emptyText: 'No Business Units' }}
                      scroll={{ x: 550 }}
                    />
                    <div className="btn-container center">
                      <Button
                        type="primary"
                        className="bia-btn"
                        loading={loading}
                        onClick={this.onSave}
                        disabled={safeMtpdDays === 0 || data.length === 0}
                      >
                        {loading ? 'Saving' : 'Save'}
                      </Button>
                    </div>
                  </div>
                </Card>
              </Col>
              <Col span={4}>
                <InformationPanel {...BIA_STEP_EXPLANATION} />
              </Col>
            </Row>
          </div>
        ) : (
          <PageLoader />
        )}
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  orgId: orgIdSelector(state),
  planId: planIdSelector(state),
  mtpdDays: mtpdDaysSelector(state),
  departmentList: departmentListSelector(state),
  tacticalBias: tacticalBiasSelector(state),
  loading: loadingTacticalBiasSelector(state),
});

const mapDispatchToProps = {
  fetchBranch,
  fetchTacticalBia,
  createTacticalBia,
  updateTacticalBia,
};

export default connect(mapStateToProps, mapDispatchToProps)(TacticalBia);
